import { formatterPrice } from 'src/utils/formatterPrice'
import { normalizeString } from 'src/utils/normalizeString'

import type {
  InstallmentItem,
  ItemPriceTag,
  RateAndBenefitIdentifier,
  Tier,
  VisualStep,
} from './types'

export const INSTALLMENT_TIERS: Tier[] = [
  { installments: 2, minCart: 99.99 },
  { installments: 4, minCart: 199.99 },
  { installments: 5, minCart: 249.99 },
  { installments: 6, minCart: 299.99 },
  { installments: 7, minCart: 699.99 },
  { installments: 8, minCart: 799.99 },
  { installments: 9, minCart: 899.99 },
  { installments: 10, minCart: 999.99 },
]

export const MAX_INSTALLMENT_TIER =
  INSTALLMENT_TIERS[INSTALLMENT_TIERS.length - 1]

export const VISUAL_STEPS: VisualStep[] = [
  { label: '2x' },
  { label: '4x' },
  { label: '6x' },
  { label: '8x' },
  { label: '10x' },
]

const CASH_DISCOUNT_BENEFIT_NAMES = [
  '[b2c] desconto 5% no pix',
  '[b2c] desconto a vista',
]

const normalizeBenefitId = (benefitId?: string) => {
  if (!benefitId) {
    return ''
  }

  return benefitId.toLowerCase().replace(/\s+/g, '').trim()
}

const isMatchingBenefitId = ({
  priceTagIdentifier,
  cashDiscountBenefitIds,
}: {
  priceTagIdentifier?: string
  cashDiscountBenefitIds: Set<string>
}) => {
  if (!priceTagIdentifier) {
    return false
  }

  if (cashDiscountBenefitIds.has(priceTagIdentifier)) {
    return true
  }

  const normalizedPriceTagIdentifier = normalizeBenefitId(priceTagIdentifier)

  if (!normalizedPriceTagIdentifier) {
    return false
  }

  return [...cashDiscountBenefitIds].some((benefitId) => {
    const normalizedBenefitId = normalizeBenefitId(benefitId)

    if (!normalizedBenefitId) {
      return false
    }

    return (
      normalizedPriceTagIdentifier === normalizedBenefitId ||
      normalizedPriceTagIdentifier.includes(normalizedBenefitId)
    )
  })
}

const isCashDiscountBenefitName = (benefitName?: string) => {
  const normalizedBenefitName = normalizeString(benefitName ?? '')
    .toLocaleLowerCase()
    .trim()

  if (!normalizedBenefitName) {
    return false
  }

  return CASH_DISCOUNT_BENEFIT_NAMES.some((cashDiscountName) =>
    normalizedBenefitName.includes(cashDiscountName)
  )
}

export const parseCurrencyLabelToNumber = (currencyLabel?: string | number) => {
  if (typeof currencyLabel === 'number') {
    return Number.isFinite(currencyLabel) ? currencyLabel : Number.NaN
  }

  if (!currencyLabel) {
    return Number.NaN
  }

  const onlyDigitsAndSeparators = currencyLabel.replace(/[^\d,.-]/g, '')
  const normalizedNumber = onlyDigitsAndSeparators
    .replace(/\./g, '')
    .replace(',', '.')

  const parsedValue = Number(normalizedNumber)

  return Number.isFinite(parsedValue) ? parsedValue : Number.NaN
}

export const roundToTwoDecimalPlaces = (value: number) => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export const formatCurrency = (value: number) => {
  return formatterPrice(value).replace(/\u00a0/g, ' ')
}

export const getCashDiscountBenefitIds = (
  rateAndBenefitsIdentifiers?: RateAndBenefitIdentifier[]
) => {
  return new Set(
    (rateAndBenefitsIdentifiers ?? [])
      .filter((benefit) => isCashDiscountBenefitName(benefit.name))
      .map((benefit) => benefit.id)
      .filter((benefitId): benefitId is string => Boolean(benefitId))
  )
}

const getSignedPriceTagValueInCurrency = (priceTag: ItemPriceTag) => {
  if (Number.isFinite(priceTag.value)) {
    return (priceTag.value as number) / 100
  }

  if (!Number.isFinite(priceTag.rawValue)) {
    return 0
  }

  const rawTagValue = priceTag.rawValue as number

  if (!Number.isInteger(rawTagValue)) {
    return rawTagValue
  }

  return rawTagValue / 100
}

export const getCashDiscountValue = ({
  items,
  cashDiscountBenefitIds,
}: {
  items?: InstallmentItem[]
  cashDiscountBenefitIds: Set<string>
}) => {
  if (!items?.length || !cashDiscountBenefitIds.size) {
    return 0
  }

  const totalCashDiscountValue = items.reduce((itemsAccumulator, item) => {
    const itemCashDiscount = (item.priceTags ?? []).reduce(
      (priceTagsAccumulator, priceTag) => {
        if (
          !isMatchingBenefitId({
            priceTagIdentifier: priceTag.identifier,
            cashDiscountBenefitIds,
          })
        ) {
          return priceTagsAccumulator
        }

        const signedPriceTagValue = getSignedPriceTagValueInCurrency(priceTag)

        if (signedPriceTagValue >= 0) {
          return priceTagsAccumulator
        }

        return priceTagsAccumulator + signedPriceTagValue
      },
      0
    )

    return itemsAccumulator + itemCashDiscount
  }, 0)

  return Math.abs(totalCashDiscountValue)
}

export const getTotalDiscountValue = (discountLabel?: string) => {
  const parsedDiscount = parseCurrencyLabelToNumber(discountLabel)

  if (!Number.isFinite(parsedDiscount)) {
    return 0
  }

  return Math.abs(parsedDiscount)
}

export const getProgressPercent = (totalCartValue: number) => {
  let reachedTierIndex = -1

  for (let index = INSTALLMENT_TIERS.length - 1; index >= 0; index -= 1) {
    if (totalCartValue >= INSTALLMENT_TIERS[index].minCart) {
      reachedTierIndex = index
      break
    }
  }

  if (reachedTierIndex < 0) {
    return 0
  }

  const reachedSteps = reachedTierIndex + 1

  return (reachedSteps / INSTALLMENT_TIERS.length) * 100
}

export const getProgressWidthClass = (progressPercent: number) => {
  if (progressPercent <= 0) {
    return 'w-0'
  }

  if (progressPercent <= 12.5) {
    return 'w-[12.5%]'
  }

  if (progressPercent <= 25) {
    return 'w-1/4'
  }

  if (progressPercent <= 37.5) {
    return 'w-[37.5%]'
  }

  if (progressPercent <= 50) {
    return 'w-1/2'
  }

  if (progressPercent <= 62.5) {
    return 'w-[62.5%]'
  }

  if (progressPercent <= 75) {
    return 'w-3/4'
  }

  if (progressPercent <= 87.5) {
    return 'w-[87.5%]'
  }

  return 'w-full'
}

export const getHighlightedStep = ({
  totalCartValue,
  nextInstallment,
}: {
  totalCartValue: number
  nextInstallment?: number
}) => {
  if (totalCartValue >= MAX_INSTALLMENT_TIER.minCart) {
    return '10x'
  }

  if (!nextInstallment) {
    return VISUAL_STEPS[0].label
  }

  const currentReachedStep = [...INSTALLMENT_TIERS]
    .reverse()
    .find((tier) => totalCartValue >= tier.minCart)

  if (!currentReachedStep) {
    return VISUAL_STEPS[0].label
  }

  return `${currentReachedStep.installments}x`
}
